/**
 * One-time notice shown on small mobile-phone browsers (not tablets), nudging
 * users toward the native Android app. Dismissed for the session via
 * sessionStorage so it doesn't keep reappearing on navigation.
 */

import { useState, useEffect } from 'preact/hooks'

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.nex.pipar'
const DISMISS_KEY = 'pipar.mobileNotice.dismissed'

/** True only for phone-sized mobile browsers; tablets and desktops return false. */
function isMobilePhoneBrowser(): boolean {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false
  const ua = navigator.userAgent || ''
  // Android tablets typically omit "Mobi" from their UA; iPads on iPadOS 13+
  // report as Macintosh with multi-touch capability.
  const hasMobiToken = /Mobi/i.test(ua)
  const isIPad = /iPad/i.test(ua) || (/Macintosh/i.test(ua) && (navigator.maxTouchPoints || 0) > 1)
  if (isIPad) return false
  if (!hasMobiToken) return false
  // Some large Android phones still report Mobi; use a width threshold to
  // exclude foldables/tablets that present a tablet-sized viewport.
  try {
    if (window.matchMedia && window.matchMedia('(min-width: 768px)').matches) return false
  } catch { /* ignore */ }
  return true
}

export function MobileBrowserNotice() {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') return
    } catch { /* ignore */ }
    if (isMobilePhoneBrowser()) setVisible(true)
  }, [])

  const handleClose = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1') } catch { /* ignore */ }
    setVisible(false)
  }

  if (!visible) return null

  return (
    <div style={overlayStyle} onClick={handleClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Use the PiPar app on your phone</div>
          <button type="button" style={closeBtnStyle} onClick={handleClose} aria-label="Close">
            {'✕'}
          </button>
        </div>
        <div style={bodyStyle}>
          <p style={paragraphStyle}>
            The PiPar web client is not optimized for mobile phones. For the best
            experience on your phone, please use the PiPar app, available on Google Play.
          </p>
          <a
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={primaryBtnStyle}
          >
            Open in Google Play
          </a>
          <button type="button" style={dismissBtnStyle} onClick={handleClose}>
            Continue in browser
          </button>
        </div>
      </div>
    </div>
  )
}

const overlayStyle: Record<string, string | number> = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: 16,
}

const modalStyle: Record<string, string | number> = {
  width: '100%',
  maxWidth: 420,
  background: '#0a1f24',
  border: '1px solid rgba(250,250,250,0.1)',
  borderRadius: 12,
  color: '#fafafa',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}

const headerStyle: Record<string, string | number> = {
  padding: '14px 18px',
  borderBottom: '1px solid rgba(250,250,250,0.1)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
}

const closeBtnStyle: Record<string, string | number> = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(250,250,250,0.5)',
  fontSize: 18,
  cursor: 'pointer',
  padding: '4px 8px',
  marginLeft: 'auto',
}

const bodyStyle: Record<string, string | number> = {
  padding: 18,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
}

const paragraphStyle: Record<string, string | number> = {
  margin: 0,
  fontSize: 14,
  lineHeight: 1.5,
  color: 'rgba(250,250,250,0.85)',
}

const primaryBtnStyle: Record<string, string | number> = {
  display: 'inline-block',
  textAlign: 'center',
  padding: '10px 16px',
  borderRadius: 8,
  background: 'rgba(45,165,160,0.85)',
  color: '#fff',
  textDecoration: 'none',
  fontSize: 14,
  fontWeight: 600,
}

const dismissBtnStyle: Record<string, string | number> = {
  padding: '8px 16px',
  border: '1px solid rgba(250,250,250,0.15)',
  borderRadius: 8,
  background: 'transparent',
  color: 'rgba(250,250,250,0.7)',
  cursor: 'pointer',
  fontSize: 13,
}
